import type { PushDevice } from '@dovefiniscono/shared';
import { api } from './api';
import { isIos, isStandalone } from './pwa';

// This browser's subscription to notifications. Everything here is about this device: what it
// receives is decided by the plan (features/notifications).

export type PushState =
  /** This browser cannot receive them. */
  | 'unsupported'
  /** iPhone and iPad: only once the app is on the Home screen. */
  | 'install-first'
  /** You said no: only the browser's settings can change it. */
  | 'denied'
  | 'off'
  | 'on';

const hasPush = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Null where no service worker runs (development). */
const registration = () => navigator.serviceWorker.getRegistration();

export async function pushState(): Promise<PushState> {
  if (!hasPush()) return isIos() && !isStandalone() ? 'install-first' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const current = await (await registration())?.pushManager.getSubscription();
  return current && Notification.permission === 'granted' ? 'on' : 'off';
}

const bytesOf = (key: string) =>
  Uint8Array.from(atob(key.replaceAll('-', '+').replaceAll('_', '/')), (c) => c.charCodeAt(0));

const asDevice = (subscription: PushSubscription) => subscription.toJSON() as PushDevice;

/** Asks the browser (it must follow a click) and tells the server. Returns the new state. */
export async function enablePush(): Promise<PushState> {
  if ((await Notification.requestPermission()) !== 'granted') return pushState();
  const ready = await navigator.serviceWorker.ready;
  const { publicKey } = await api.notifications.key();
  const key = bytesOf(publicKey);

  // A subscription made for another account has another key: it cannot be reused.
  const existing = await ready.pushManager.getSubscription();
  const reusable =
    existing?.options.applicationServerKey &&
    new Uint8Array(existing.options.applicationServerKey).every((byte, i) => byte === key[i]);
  if (existing && !reusable) await existing.unsubscribe();

  const subscription = reusable
    ? existing
    : await ready.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await api.notifications.addDevice(asDevice(subscription));
  return 'on';
}

export async function disablePush(): Promise<void> {
  const subscription = await (await registration())?.pushManager.getSubscription();
  if (!subscription) return;
  await api.notifications.removeDevice(subscription.endpoint);
  await subscription.unsubscribe();
}

/** Browsers can change the address on their own: each visit tells the server the current one. */
export async function refreshPush(): Promise<void> {
  if (!hasPush() || Notification.permission !== 'granted') return;
  const subscription = await (await registration())?.pushManager.getSubscription();
  if (subscription) await api.notifications.addDevice(asDevice(subscription));
}

/**
 * Leaving this browser: no more notifications for an account that is not here any more. The
 * browser's own unsubscribe is enough for the server to drop the device at the next attempt.
 */
export async function forgetPush(session: 'open' | 'gone'): Promise<void> {
  if (!hasPush()) return;
  const subscription = await (await registration())?.pushManager.getSubscription();
  if (!subscription) return;
  if (session === 'open')
    await api.notifications.removeDevice(subscription.endpoint).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
}
